import { FC, ReactElement, useEffect, useMemo } from 'react'
import MenuItem from '@mui/material/MenuItem'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import { Form } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CampaignListParams } from '../campaigns/interfaces/campaigns'
import { useAppDispatch, useAppSelector } from '../core/redux/hooks'
import { getAllAgencies } from '../agencies/slices'
import { Box, Checkbox, ListItemText } from '@mui/material'
import { RoleType } from '../core/routes/constants'
import { FilterCampaignStatus } from '../campaigns/constants'
import { getCategories } from '../campaign-categories/slices'
import {
  AgenciesSelectItems,
  StaticSelectItems,
  allText,
  onCategorySelectChange,
  selectMenuItem
} from '../commons/utils/utils'
import { chip } from './utils'
import SelfServeSearchField from '../ui/search-field/SelfServeSearchField'
import SelfServeCustomSelect from '../ui/select/SelfServeCustomSelect'

interface CampaignsListHeaderProps {
  handleFormChange: (changedData: CampaignListParams) => void
  search?: string
  agency?: string
  status?: string
  categories: string[]
}

const CampaignsListHeader: FC<CampaignsListHeaderProps> = ({
  handleFormChange,
  search,
  agency,
  status,
  categories
}): ReactElement => {
  const { t } = useTranslation()
  const { agencies, userType, tenantID, isAgencyEnabled, campaignCategories } =
    useAppSelector((state) => ({
      agencies: state.agencies.agencies,
      userType: state.auth.userType,
      tenantID: state.auth.tenantID,
      isAgencyEnabled: state.auth.isAgencyEnabled,
      campaignCategories: state.campaignCategories
    }))

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (userType !== RoleType.AGENCY && isAgencyEnabled) {
      void dispatch(getAllAgencies({ tenantID }))
    }
    void dispatch(getCategories({ tenantID, agencyID: agency }))
  }, [tenantID, dispatch, userType, agency, isAgencyEnabled])

  const categoriesSelectItems = useMemo(
    () =>
      campaignCategories.categories.map((category) => (
        <MenuItem
          className='multi-select-item'
          disableRipple
          disableTouchRipple
          key={category.CampaignCategoryID}
          value={category.CampaignCategoryID?.toString()}
        >
          <Checkbox
            disableRipple
            disableFocusRipple
            disableTouchRipple
            checked={categories.includes(
              category.CampaignCategoryID?.toString() ?? ''
            )}
          />
          <ListItemText primary={category.CategoryName} />
        </MenuItem>
      )),
    [campaignCategories.categories, categories]
  )

  return (
    <div>
      <Form role='search'>
        <FormControl className='form-control'>
          <SelfServeSearchField value={search} onChange={handleFormChange} />
        </FormControl>
        {userType !== RoleType.AGENCY && isAgencyEnabled && (
          <FormControl className='form-control' size='small'>
            <InputLabel shrink={false} id='agencies-label'>
              {(agency == null || agency === '') && allText(t`AgencyForSelect`)}
            </InputLabel>
            <SelfServeCustomSelect
              handleFormChange={(e) =>
                handleFormChange({ agencyID: e.target.value as string })}
              value={agency}
              menuItem={selectMenuItem(t`AllAgencies`)}
              selectItems={AgenciesSelectItems(agencies)}
              inputProps='table-input maxw-8 minw-8'
            />
          </FormControl>
        )}
        <FormControl size='small' className='form-control'>
          {categories?.length === 0 && (
            <InputLabel shrink={false} id='categories-label'>
              {(categories == null || categories?.length === 0) &&
                allText(t`CategoriesSelect`)}
            </InputLabel>
          )}
          <SelfServeCustomSelect
            handleFormChange={(e) =>
              onCategorySelectChange(e, handleFormChange)}
            value={categories}
            menuItem={selectMenuItem(t`AllCategories`)}
            selectItems={categoriesSelectItems}
            inputProps='table-input minw-7'
            native={false}
            displayEmpty
            renderValue={() => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {categories.map((value) =>
                  chip(value, campaignCategories.categories)
                )}
              </Box>
            )}
            multiple
          />
        </FormControl>
        <FormControl className='form-control' size='small'>
          <InputLabel shrink={false} id='status-label'>
            {(status == null || status === '') && allText(t`StatusForSelect`)}
          </InputLabel>
          <SelfServeCustomSelect
            handleFormChange={(e) =>
              handleFormChange({ status: e.target.value as string })}
            value={status}
            inputProps='table-input maxw-8 minw-8'
            menuItem={selectMenuItem(t`AllStatus`)}
            selectItems={StaticSelectItems(FilterCampaignStatus)}
          />
        </FormControl>
      </Form>
    </div>
  )
}

export default CampaignsListHeader
